import plur from 'plur';
import mysqlLogoUrl from '@/assets/images/integrations/mysql.svg';
import postgresqlLogoUrl from '@/assets/images/integrations/postgresql.svg';
import snowflakeLogoUrl from '@/assets/images/integrations/snowflake.svg';
import bigqueryLogoUrl from '@/assets/images/integrations/bigquery.svg';
import redshiftLogoUrl from '@/assets/images/integrations/redshift.svg';
import sqlserverLogoUrl from '@/assets/images/integrations/sqlserver.svg';
import type { SqlImport } from '@/services/db/sql-import/entity';
import { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { DataFlow, IntegrationCategory, IntegrationType } from '../catalog/enums';
import { IntegrationIDs, IntegrationNames } from '../catalog/ids';
import type { SynthesizedIntegration } from './types';

// Re-exported so the feature's public API surface stays stable for consumers.
export { SqlDatabaseType, type SqlImport };

function integrationId(type: SqlDatabaseType): string {
  return {
    [SqlDatabaseType.mysql]: IntegrationIDs.mysql,
    [SqlDatabaseType.postgres]: IntegrationIDs.postgres,
    [SqlDatabaseType.snowflake]: IntegrationIDs.snowflakeIn,
    [SqlDatabaseType.bigQuery]: IntegrationIDs.bigQueryIn,
    [SqlDatabaseType.redshift]: IntegrationIDs.redshiftIn,
    [SqlDatabaseType.sqlserver]: IntegrationIDs.sqlserver,
  }[type];
}

function integrationName(type: SqlDatabaseType): string {
  return {
    [SqlDatabaseType.mysql]: IntegrationNames.mysql,
    [SqlDatabaseType.postgres]: IntegrationNames.postgres,
    [SqlDatabaseType.snowflake]: IntegrationNames.snowflakeIn,
    [SqlDatabaseType.bigQuery]: IntegrationNames.bigQueryIn,
    [SqlDatabaseType.redshift]: IntegrationNames.redshiftIn,
    [SqlDatabaseType.sqlserver]: IntegrationNames.sqlserver,
  }[type];
}

function imageSrc(type: SqlDatabaseType): string {
  return {
    [SqlDatabaseType.mysql]: mysqlLogoUrl,
    [SqlDatabaseType.postgres]: postgresqlLogoUrl,
    [SqlDatabaseType.snowflake]: snowflakeLogoUrl,
    [SqlDatabaseType.bigQuery]: bigqueryLogoUrl,
    [SqlDatabaseType.redshift]: redshiftLogoUrl,
    [SqlDatabaseType.sqlserver]: sqlserverLogoUrl,
  }[type];
}

function overview(type: SqlDatabaseType, peoplePlural: string): string {
  return {
    [SqlDatabaseType.mysql]: `Sync ${peoplePlural}/objects from your MySQL database to Customer.io`,
    [SqlDatabaseType.postgres]: `Sync ${peoplePlural}/objects from your PostgreSQL database to Customer.io`,
    [SqlDatabaseType.snowflake]: `Sync ${peoplePlural}/objects from Snowflake to Customer.io`,
    [SqlDatabaseType.bigQuery]: `Sync ${peoplePlural}/objects from BigQuery to Customer.io`,
    [SqlDatabaseType.redshift]: `Sync ${peoplePlural}/objects from your Redshift database to Customer.io`,
    [SqlDatabaseType.sqlserver]: `Sync ${peoplePlural}/objects from SQL Server to Customer.io`,
  }[type];
}

/**
 * Map a database's SQL-import rows to a synthesized directory entry. Snowflake /
 * BigQuery / Redshift resolve to their `*In` ids (this is the `bigquery` →
 * `bigquery-in` id bridge); mysql / postgres / sqlserver use the plain id.
 */
export function mapSqlDatabaseIntegration(
  sqlImports: readonly SqlImport[],
  type: SqlDatabaseType,
  peoplePlural = 'people',
): SynthesizedIntegration {
  const total = sqlImports.length;
  const totalActive = sqlImports.filter((sqlImport) => sqlImport.active === true).length;

  return {
    id: integrationId(type),
    forceOrder: 0,
    imageSrc: imageSrc(type),
    name: integrationName(type),
    overview: overview(type, peoplePlural),
    enabledStatus: totalActive > 0 ? `${totalActive} active ${plur('sync', totalActive)}` : 'No active syncs',
    category: IntegrationCategory.databases,
    enabled: total > 0,
    beta: false,
    type: IntegrationType.native,
    publicType: IntegrationType.existing,
    path: 'sql-imports',
    dynamicSegment: type,
    dataFlow: DataFlow.in,
    total,
  };
}
